/**
 * The two things done *to* a member from the detail page that are not edits:
 * an amendment, and a print.
 *
 * An amendment is one of nine server-side actions; which ones this account
 * can take right now comes from the server too, so the picker never offers
 * what would be refused. Each action asks only for what it needs — a reason
 * from the configured list, a tier, a primary account — and answers with the
 * member as it now stands.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/dialog";
import { Field, Form, Input } from "@/components/ui/field";
import { OptionSelect } from "@/components/ui/option-select";
import { api, errorMessage } from "@/services/api";
import { MembershipCardPrint, cardFileName } from "@/components/member-print-card";
import { useMemberChannels } from "@/components/member-acquisition";
import { PrimaryMoveDialog, type PrimaryMove } from "@/components/member-primary-move";
import { type TranslationKey, type Translate, translate } from "@/constants/i18n";
import {
  ACQUISITION,
  ADD_MEMBER_ACQUISITION,
  MEMBERSHIP_TYPE,
  type Member,
  TIER,
  day,
  label,
} from "@/pages/MembersPage";
import { useTranslation } from "@/store/locale";

export type Amendment =
  | "blacklist"
  | "cancellation"
  | "card-replacement"
  | "upgrade"
  | "downgrade"
  | "change-tier"
  | "activate"
  | "account-renew"
  | "change-primary-account";

const LABELS: Record<Amendment, TranslationKey> = {
  blacklist: "md.blacklist",
  cancellation: "md.cancellation",
  "card-replacement": "md.cardReplacement",
  upgrade: "md.upgrade",
  downgrade: "md.downgrade",
  "change-tier": "md.changeTier",
  activate: "md.activate",
  "account-renew": "md.accountRenew",
  "change-primary-account": "md.changePrimaryAccount",
};

/** Which configured reason list an action's reason comes from. */
const REASON_KIND: Partial<Record<Amendment, string>> = {
  blacklist: "blacklist",
  cancellation: "cancellation",
  "card-replacement": "card_replacement",
};
const NEEDS_TIER = new Set<Amendment>(["upgrade", "change-tier", "activate"]);
/**
 * The two tiers an upgrade does not open on.
 *
 * Neither issues a supplementary card at all — see the numbering matrix — so a
 * card on one of them is not a membership that carries over to a primary of
 * its own, and legacy left the picker empty rather than proposing it.
 */
const KEPT_BY_NOBODY = new Set(["affiliates", "employee"]);
/**
 * The two that ask for a primary account number, and are asked in a sheet of
 * their own: the number is looked up, the account it names is shown, and the
 * amendment waits for that to be confirmed. See `PrimaryMoveDialog`.
 */
const NEEDS_PARENT = new Set<Amendment>(["downgrade", "change-primary-account"]);
/**
 * Whether the chosen reason is the list's "Others", the one that asks what
 * actually happened.
 *
 * Nothing is appended to these pickers any more. The list is whatever
 * Configuration holds and nothing besides — this modal used to add an "Others"
 * of its own, which meant Card Replacement held three options and offered
 * four, two of them reading "Others". The configured row is the one that
 * stays, and picking it still opens the box: filing the bare word says
 * nothing, and the words are what the amendment is for.
 *
 * Matched against the stored word and against the rendered label both. The
 * rows are data and come back in English whatever the interface is set to, and
 * a list reworded locally should still ask.
 */
const isOthers = (text: string, label: string) => {
  const normal = singular(text);
  return normal !== "" && (normal === "other" || normal === singular(label));
};

/**
 * Trimmed, lowercased, and with a trailing "s" taken off.
 *
 * The lists disagree with each other and always have: blacklist says "Other",
 * card replacement and endorsement say "Others". They are the same option, and
 * the picker that asked for an exact "others" quietly skipped the box on
 * blacklist alone. Comparing singulars settles it in one place, whichever way
 * a list happens to be worded — here or in Configuration, where somebody may
 * reword it again tomorrow.
 */
const singular = (text: string) => text.trim().toLowerCase().replace(/s$/, "");

interface Reason {
  id: number;
  reason: string;
}

/** The actions this member can take right now, from the server. */
export function useAllowedAmendments(memberId: number) {
  return useQuery({
    queryKey: [`/members/${memberId}/`, "amendments"],
    queryFn: async ({ signal }) =>
      (await api.get<{ allowed: Amendment[] }>(`/members/${memberId}/amendments`, { signal }))
        .data.allowed,
  });
}

export function AmendmentDialog({
  memberId,
  action,
  currentTier,
  currentAcquisition,
  currentAccountNumber,
  currentParentAccountNumber,
  onClose,
  onDone,
}: {
  memberId: number;
  action: Amendment | null;
  currentTier: string | null;
  /** The channel the account holds now, to offer back when it still applies. */
  currentAcquisition?: string | null;
  /** Its own account number: nothing can be made its own primary. */
  currentAccountNumber?: string | null;
  /** The primary it hangs off now, when it hangs off one. */
  currentParentAccountNumber?: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useTranslation();
  const [reason, setReason] = useState("");
  const [others, setOthers] = useState("");
  /**
   * The tier the amendment leaves the account on.
   *
   * An **upgrade** opens on the tier it is already on, as legacy opened it: a
   * supplementary becoming a primary of its own keeps its membership, so the
   * answer is known and the clerk is confirming it rather than choosing.
   * Affiliates and employee are left blank there, as legacy left them — a card
   * on one of those is not a membership that carries over.
   *
   * A **change of tier** opens blank, because that one is a decision to move:
   * a tier already filled in would be one nobody made, and the tier the
   * account is on is not even on its list.
   */
  const [tier, setTier] = useState(
    action === "upgrade" && currentTier && !KEPT_BY_NOBODY.has(currentTier) ? currentTier : "",
  );
  // Legacy pre-selected the account's own channel when moving to Member, but
  // only when it was one a member can have: an account coming back from
  // affiliates holds Benefits, which is not an answer here, so the picker opens
  // empty and the move waits for a real one.
  const memberChannels = useMemberChannels(currentAcquisition);
  const [acquisition, setAcquisition] = useState(
    currentAcquisition && currentAcquisition !== "benefits" ? currentAcquisition : "",
  );
  const [employeeNumber, setEmployeeNumber] = useState("");
  const [approvedBy, setApprovedBy] = useState("");
  const [error, setError] = useState<string | null>(null);

  const kind = action ? REASON_KIND[action] : undefined;
  // Every list this dialog can need, from the amendment rather than from the
  // Configuration screens.
  //
  // These used to come off `GET /reasons?kind=…`, which is the Notes and
  // Reasons tabs' own list and asks for their rights — `view__reason` for a
  // blacklist or a cancellation, `view__notes_complimentary` for the approver.
  // A Supervisor holds none of them, so every reason picker in every amendment
  // dialog opened empty for them: five approvers on file and a dropdown
  // showing none of them. Being able to amend a member is not the same as
  // being able to administer the lists of reasons, and a picker only needs the
  // first.
  //
  // One request for all four lists, so a dropdown does not appear a beat after
  // the dialog it sits in.
  const options = useQuery({
    queryKey: [`/members/${memberId}/amendments/reasons`],
    queryFn: async ({ signal }) =>
      (
        await api.get<{ reasons: Record<string, { id: number; name: string | null }[]> }>(
          `/members/${memberId}/amendments/reasons`,
          { signal },
        )
      ).data.reasons,
    enabled: !!action,
    staleTime: 300_000,
  });
  const listFor = (want: string | undefined) =>
    (want ? (options.data?.[want] ?? []) : []).map((one) => ({
      id: one.id,
      reason: one.name,
    })) as Reason[];
  const reasons = { data: listFor(kind) };
  const approvers = { data: listFor("complimentary_approved_by") };

  const apply = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post(`/members/${memberId}/amendments/${action}`, body),
    onSuccess: () => {
      toast.success(t("md.amendmentApplied"));
      onDone();
    },
    onError: (err) => setError(errorMessage(err, t("md.amendmentFailed"))),
  });

  if (!action) return null;

  // The two that ask for a primary are a sheet of their own: a number typed
  // here reissues a card under somebody else's account, so it is looked up,
  // shown, and confirmed rather than taken on trust from a text box.
  if (NEEDS_PARENT.has(action))
    return (
      <PrimaryMoveDialog
        action={action as PrimaryMove}
        memberId={memberId}
        accountNumber={currentAccountNumber}
        parentAccountNumber={currentParentAccountNumber}
        currentAcquisition={currentAcquisition}
        onClose={onClose}
        onDone={onDone}
      />
    );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (kind) {
      // "Others" files the words, not the word: an amendment recorded as
      // "Others" and nothing else tells whoever reads it back nothing at all.
      const text = isOthers(reason, t("md.others")) ? others.trim() : reason;
      if (!text) return setError(t("md.reasonRequired"));
      return apply.mutate({ reason: text });
    }
    if (NEEDS_TIER.has(action)) {
      if (!tier) return setError(t("md.tierRequired"));
      if (tier === "employee" && !employeeNumber.trim())
        return setError(t("md.employeeNumberRequired"));
      // The other three tiers are acquired by benefits and the server fills it
      // in; a member is acquired through a channel somebody has to name.
      if (tier === "member" && !acquisition) return setError(t("md.acquisitionRequired"));
      return apply.mutate({
        membership_tier: tier,
        employee_number: employeeNumber.trim() || null,
        approved_by_id: approvedBy ? Number(approvedBy) : null,
        ...(tier === "member" ? { acquisition_type: acquisition } : {}),
      });
    }
    // account-renew: ten minutes, nothing to ask.
    return apply.mutate({});
  };

  // A change of tier is a change: the tier the account is already on is not one
  // of the answers, and legacy's TIER_OPTIONS_MATRIX leaves it out of every row
  // for this action. An upgrade or an activate may well land on the same tier,
  // so they keep the full list.
  const tiers =
    action === "change-tier" ? TIER(t).filter((o) => o.value !== currentTier) : TIER(t);

  const formId = "amendment-form";
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={t(LABELS[action])}
      description={t(`md.amend.${action}` as TranslationKey)}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form={formId} loading={apply.isPending}>
            {t("md.apply")}
          </Button>
        </>
      }
    >
      <Form id={formId} className="grid gap-4 pb-2" onSubmit={submit}>
        {kind ? (
          <>
            <Field label={t("hist.reason")} htmlFor="amendment-reason">
              <OptionSelect
                id="amendment-reason"
                value={reason}
                onChange={setReason}
                placeholder={t("md.selectReason")}
                choices={(reasons.data ?? []).map((r) => ({
                  value: r.reason,
                  label: r.reason,
                }))}
              />
            </Field>
            {isOthers(reason, t("md.others")) ? (
              <Field label={t("md.specifyReason")} htmlFor="amendment-others">
                <Input
                  id="amendment-others"
                  autoFocus
                  value={others}
                  onChange={(e) => setOthers(e.target.value)}
                />
              </Field>
            ) : null}
          </>
        ) : null}
        {NEEDS_TIER.has(action) ? (
          <>
            <Field label={t("mem.membershipTier")} htmlFor="amendment-tier">
              <OptionSelect
                id="amendment-tier"
                value={tier}
                onChange={setTier}
                placeholder={t("md.selectTier")}
                // A tier is always one of the four; there is no empty tier to
                // go back to, so the list does not offer one.
                clearable={false}
                choices={tiers}
              />
            </Field>
            {/* Shown on every tier, as legacy showed it. On the three the club
                signs up itself it reads Benefits and cannot be touched — the
                answer is already known, and hiding the field would leave the
                reader wondering what the account will end up recorded as. */}
            <Field label={t("mem.acquisitionType")} htmlFor="amendment-acquisition">
              {!tier ? (
                <OptionSelect
                  id="amendment-acquisition"
                  value=""
                  onChange={() => {}}
                  disabled
                  placeholder={t("new.selectAcquisition")}
                  choices={ACQUISITION(t)}
                />
              ) : tier === "member" ? (
                <OptionSelect
                  id="amendment-acquisition"
                  value={acquisition}
                  onChange={setAcquisition}
                  placeholder={t("new.selectAcquisition")}
                  // A member's channels, and which of them this person may
                  // set. The two branches either side of this one are the
                  // other tiers' — fixed at Benefits, which is not a choice
                  // and not anybody's to widen.
                  choices={memberChannels}
                />
              ) : (
                <OptionSelect
                  id="amendment-acquisition"
                  value="benefits"
                  onChange={() => {}}
                  disabled
                  clearable={false}
                  choices={ACQUISITION(t)}
                />
              )}
            </Field>
            {tier === "employee" ? (
              <Field label={t("mem.employeeNumber")} htmlFor="amendment-employee">
                <Input
                  id="amendment-employee"
                  value={employeeNumber}
                  onChange={(e) => setEmployeeNumber(e.target.value)}
                />
              </Field>
            ) : null}
            {tier === "complimentary" ? (
              <Field label={t("new.approvedBy")} htmlFor="amendment-approved">
                <OptionSelect
                  id="amendment-approved"
                  value={approvedBy}
                  onChange={setApprovedBy}
                  placeholder={t("new.approvedBy")}
                  choices={(approvers.data ?? []).map((r) => ({
                    value: String(r.id),
                    label: r.reason,
                  }))}
                />
              </Field>
            ) : null}
          </>
        ) : null}
        {error ? (
          <p role="alert" className="text-danger text-callout">
            {error}
          </p>
        ) : null}
      </Form>
    </Modal>
  );
}

/** A row of `GET /members/{id}/print-history`, as much as this screen reads. */
interface PrintJob {
  id: number;
  print_reason: string;
  print_status: "pending" | "printed" | "failed";
  additional_reason: string;
}

/**
 * Legacy's Print Reason list (global enum 17), in its order and its words.
 *
 * The keys are the table's own — `member-histories.tsx` resolves a stored
 * reason back through the same labels — so the reason this dialog writes is
 * read back in the words it was chosen by, rather than in a second vocabulary
 * that has to be kept in step by hand.
 */
const PRINT_REASONS: [string, TranslationKey][] = [
  ["renew", "hist.renew"],
  ["new", "hist.new"],
  ["lost", "hist.lost"],
  ["damaged", "hist.damaged"],
  ["upgrade", "hist.upgrade"],
  ["downgrade", "hist.downgrade"],
  ["activate", "hist.activate"],
  ["change_tier", "hist.changeTier"],
  ["transfer", "hist.transfer"],
  ["others", "hist.others"],
];

/**
 * Print Preview: the card as it will come out, and the one question about it.
 *
 * Ported from legacy's print preview — the reason above, the card below, Close
 * and Confirm across the foot. The print is recorded on the server first (the
 * pending job is marked printed and the member leaves the queue) and only then
 * handed to the printer, so a card on paper is a card on record.
 *
 * **The reason is not asked for when the answer is already known.** A print is
 * usually the tail of something else — an enrolment, an upgrade, a replacement
 * — and that act queued a job whose reason is written on it. The dialog reads
 * the member's print history, takes the pending job's reason, and shows it
 * filled in and locked, as legacy locked it against the amendment it came from.
 * Only a reprint with nothing pending is a question, and then the picker opens.
 */
export function PrintDialog({
  member,
  open,
  onClose,
  onPrinted,
}: {
  member: Member;
  open: boolean;
  onClose: () => void;
  onPrinted: () => void;
}) {
  const t = useTranslation();
  const [chosen, setChosen] = useState("");
  const [others, setOthers] = useState("");
  const [error, setError] = useState<string | null>(null);

  const history = useQuery({
    queryKey: [`/members/${member.id}/print-history`],
    queryFn: async ({ signal }) =>
      (await api.get<PrintJob[]>(`/members/${member.id}/print-history`, { signal })).data,
    enabled: open,
  });
  // The job the enrolment or the amendment raised, newest first — what this
  // print is *for*. The API takes the same one when the body names no job, so
  // the two cannot disagree about which card is being printed.
  const pending = (history.data ?? []).find((job) => job.print_status === "pending");
  /**
   * Whether a card has ever actually come out for this member.
   *
   * A pending job is a card queued, not a card printed: an enrolment raises
   * one, and every amendment before the first print raises another over it. So
   * the newest pending job on a member who has never held a card says
   * "Downgrade" when what the printer is about to produce is their first card.
   *
   * The first print is New, whatever was done to the account on the way to it —
   * the amendments are on the member's own record, and this is still the first
   * piece of plastic. Only once one has been printed does the next print carry
   * the reason of the amendment that asked for it.
   */
  const firstPrint = !(history.data ?? []).some((job) => job.print_status === "printed");
  const reason = pending ? (firstPrint ? "new" : pending.print_reason) : chosen;
  const specific = pending ? pending.additional_reason : others;
  const locked = !!pending;

  /**
   * The job this sheet was opened for, held from the moment it opened.
   *
   * The same card can be in front of two people at once — two tabs, two tills.
   * One Confirm takes the job: the server locks the row and refuses a second,
   * so a card is never printed twice on the strength of one amendment. But the
   * sheet that lost would have found nothing pending a moment later, quietly
   * turned into a reprint, and printed a duplicate card under a job of its own
   * — a worse answer than the refusal it was spared.
   *
   * The stream is what brings the news: the print writes to the member, the
   * server publishes it, and every screen showing that member re-reads.
   */
  const [openedFor, setOpenedFor] = useState<number | null>(null);
  useEffect(() => {
    if (!open) setOpenedFor(null);
  }, [open]);
  useEffect(() => {
    if (open && openedFor === null && pending) setOpenedFor(pending.id);
  }, [open, openedFor, pending]);
  const opened = (history.data ?? []).find((job) => job.id === openedFor);
  const takenElsewhere = openedFor !== null && opened?.print_status !== "pending";

  const print = useMutation({
    mutationFn: () =>
      api.post(
        `/members/${member.id}/print`,
        pending
          ? // The job goes out under the reason it was raised with, even when
            // the sheet above read New. The two are answering different
            // questions: the sheet says what this piece of plastic is — the
            // member's first card — and the row says what asked for it, which
            // is the change of tier that queued it. Sending a reason here
            // would write over that and lose the amendment from the history.
            { print_history_id: pending.id }
          : {
              print_reason: chosen || null,
              additional_reason: chosen === "others" ? others.trim() : "",
            },
      ),
    onSuccess: () => {
      toast.success(t("md.printRecorded"));
      onPrinted();
      // Printed while the sheet is still up: the card on screen *is* the page
      // the printer is given (see `#print-card-root` in index.css), so closing
      // first would hand it a page with no card on it.
      // The page title is the file name "Save as PDF" offers, so for the
      // length of the print it is the member's own rather than the app's.
      // Put back once the dialog is done with it: `afterprint`, since not
      // every browser holds the script at window.print() until then.
      //
      // Inside a frame, Chrome names the PDF after the *top* page's title, not
      // the frame's — the print header said the member, the file was still the
      // host's name. So the top page is retitled too, where the browser lets
      // a frame reach it (same origin); a host on another origin keeps its
      // own title and only the frame's changes.
      const name = cardFileName(member);
      const retitled: [Document, string][] = [];
      const retitle = (doc: Document) => {
        retitled.push([doc, doc.title]);
        doc.title = name;
      };
      retitle(document);
      try {
        if (window.top && window.top !== window) retitle(window.top.document);
      } catch {
        // Cross-origin host: not ours to rename.
      }
      window.addEventListener(
        "afterprint",
        () => retitled.forEach(([doc, title]) => (doc.title = title)),
        { once: true },
      );
      window.print();
      onClose();
    },
    onError: (err) => setError(errorMessage(err, t("md.printFailed"))),
  });

  const confirm = () => {
    setError(null);
    if (takenElsewhere) return;
    if (!locked && !reason) return setError(t("md.printReasonRequired"));
    if (!locked && reason === "others" && !others.trim())
      return setError(t("md.reasonRequired"));
    print.mutate();
  };

  // English on the card whatever the screen is in: the plastic is printed one
  // way, and the tier on it is the tier S&R prints.
  const en: Translate = (key) => translate("en", key);
  const cardLabel = [
    label(MEMBERSHIP_TYPE(en), member.membership_type),
    label(TIER(en), member.membership_tier),
  ]
    .filter((part) => part !== "—")
    .join(" ");

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="sm:max-w-[35rem]"
      title={t("md.printPreview")}
      description={t("md.printPreviewBody")}
      footer={
        <>
          <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
            {t("common.close")}
          </Button>
          <Button
            type="button"
            className="flex-1"
            onClick={confirm}
            loading={print.isPending}
            loadingLabel={t("md.printing")}
            disabled={history.isPending || takenElsewhere}
          >
            {t("common.confirm")}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 pb-2">
        <Field label={t("md.printReason")} htmlFor="print-reason">
          <OptionSelect
            id="print-reason"
            value={reason}
            onChange={setChosen}
            placeholder={t("md.printReasonHint")}
            disabled={locked || history.isPending}
            choices={PRINT_REASONS.map(([value, key]) => ({ value, label: t(key) }))}
          />
        </Field>
        {/* Legacy showed the words behind an "Others" the same way: there to
            read, not to rewrite, because the act that queued the print wrote
            them. */}
        {reason === "others" && locked ? (
          <Field label={t("md.specifyReason")} htmlFor="print-others">
            <Input id="print-others" value={specific} disabled readOnly />
          </Field>
        ) : null}
        {reason === "others" && !locked ? (
          <Field label={t("md.specifyReason")} htmlFor="print-others">
            <Input
              id="print-others"
              value={others}
              onChange={(event) => setOthers(event.target.value)}
            />
          </Field>
        ) : null}
        {takenElsewhere ? (
          <p role="alert" className="text-danger text-callout">
            {t("md.printTakenElsewhere")}
          </p>
        ) : null}
        {/* Not while the line above is up. A Confirm that discovers the card
            was printed elsewhere comes back with the service saying so too, and
            the sheet then said the same thing twice — once as the standing
            condition, once as the failure that found it. Every other failure
            still speaks: this one is only hidden because it is already said. */}
        {error && !takenElsewhere ? (
          <p role="alert" className="text-danger text-callout">
            {error}
          </p>
        ) : null}
        {/* The card is 3.75in wide and does not reflow — it is a picture of a
            piece of plastic. On a frame narrower than it, legacy shrinks the
            preview to 86% from its top edge (`__print-preview`, its 420px
            rule); the scroller is this app's own half of the answer, since a
            transform leaves the box it was laid out in and the sheet would
            still have been pushed wide by the unscaled 360 pixels. */}
        <div className="bg-surface-sunken flex justify-center overflow-x-auto rounded-xl p-5">
          <div className="shrink-0 max-[420px]:origin-top max-[420px]:scale-[0.86]">
            <MembershipCardPrint
              member={member}
              label={cardLabel}
              since={day(member.created_at)}
            />
          </div>
        </div>
      </div>
      {/**
       * The page the printer is given, and the whole of it.
       *
       * Hiding the rest of the app with `visibility` was not enough: a hidden
       * element keeps its box, so the member's page went on paginating behind
       * the card and every print came out two pages, the second one blank. The
       * printed copy is therefore its own child of `<body>`, and `index.css`
       * hides every sibling outright while printing — one element in the
       * document, one page out of it.
       *
       * A second render of the same card rather than a moved one: the sheet
       * needs its preview on screen at the same time, and the component is a
       * pure function of the member, so the two cannot drift.
       */}
      {createPortal(
        <div id="print-card-root">
          <MembershipCardPrint
            member={member}
            label={cardLabel}
            since={day(member.created_at)}
          />
        </div>,
        document.body,
      )}
    </Modal>
  );
}
